import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Form } from "react-bulma-components";
import { enumeratedDataValuesSlice } from "redux/features/enumeratedDataValues";
import dayjs from "dayjs";
import { RadioColumns } from "util/optionColumns";

import { PromptPhdYear } from "components/shared/phdYear";

const { Field, Control, Label } = Form;

function comparePrePhdLevels(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  // Check only the keys. If the same set of keys are present (e.g., user
  // selected a different level, but then selected the same level again)
  // no need to consider this a "change" even though the timestamp value
  // will have been updated.
  const levelsA = Object.keys(a).sort();
  const levelsB = Object.keys(b).sort();
  if (levelsA.length !== levelsB.length) return false;
  const differences = levelsA
    .map((k, i) => k !== levelsB[i])
    .filter((difference) => !!difference);
  return differences.length === 0;
}

function EducationLevelComponent({
  orig,
  getValue,
  setValue,
  forSummary,
  RenderField,
}) {
  const enumeratedDataValues = useSelector(enumeratedDataValuesSlice.selector);

  const educationLevels = enumeratedDataValues.data.education_levels;
  const occupations = enumeratedDataValues.data.occupations;

  const educationLevelLookup = Object.fromEntries(
    educationLevels.map((e) => [e.symbolic_id, e])
  );

  const occupationLookup = Object.fromEntries(
    occupations.map((e) => [e.symbolic_id, e])
  );

  const storedInfo = getValue("education_level_info");

  const fromCurrentOccupation = storedInfo.from_current_occupation
    ? educationLevelLookup[storedInfo.education]
    : null;

  const fromPastOccupation = storedInfo.past_occupation_education
    ? educationLevelLookup[storedInfo.past_occupation_education]
    : null;

  const pastOccupationInfo = fromPastOccupation
    ? occupationLookup[fromPastOccupation.symbolic_id]
    : null;

  const phdYear = getValue("phd_year");
  const prePhdLevels = getValue("pre_phd_education_levels");

  let selection = fromCurrentOccupation ?? fromPastOccupation;

  if (phdYear !== null) selection = educationLevelLookup.PHD;

  const preferNotToSay =
    !selection && prePhdLevels && Object.keys(prePhdLevels).length === 0;

  if (prePhdLevels && !selection) {
    educationLevels
      .filter((levelInfo) => levelInfo.symbolic_id !== "PHD")
      .filter((levelInfo) => !selection || levelInfo.level > selection.level)
      .forEach((levelInfo) => {
        if (prePhdLevels[levelInfo.symbolic_id]) selection = levelInfo;
      });
  }

  const selectLevel = (levelInfo) => {
    let setPrePhdLevel = null;
    const id = levelInfo.symbolic_id;

    if (!fromPastOccupation || levelInfo.level > fromPastOccupation.level) {
      if (levelInfo.symbolic_id === "PHD") {
        if (phdYear === null) setValue("phd_year", orig.phd_year ?? "");
      } else {
        setPrePhdLevel = id;
      }
    }

    const removePrePhdLevels = educationLevels
      .filter(
        (otherInfo) =>
          otherInfo.level > levelInfo.level && otherInfo.symbolic_id !== "PHD"
      )
      .map((otherInfo) => otherInfo.symbolic_id);

    if (id !== "PHD") setValue("phd_year", null);

    const newPrePhdLevels = prePhdLevels ? { ...prePhdLevels } : {};
    removePrePhdLevels.forEach((otherLevelId) => {
      delete newPrePhdLevels[otherLevelId];
    });

    if (setPrePhdLevel)
      newPrePhdLevels[setPrePhdLevel] = dayjs().format("YYYY-MM-DD");

    setValue(
      "pre_phd_education_levels",
      Object.keys(newPrePhdLevels).length === 0 ? null : newPrePhdLevels,
      comparePrePhdLevels
    );
  };

  const setPreferNotToSay = (bool) => {
    setValue("pre_phd_education_levels", {}, comparePrePhdLevels);
    setValue("phd_year", null);
  };

  const radioSelection = selection
    ? selection.symbolic_id
    : preferNotToSay
    ? "PREFER_NOT_TO_SAY"
    : null;

  return (
    <Fragment>
      <RenderField
        label="Education"
        question="What is the highest level of education you have undertaken?"
        value={
          selection
            ? selection.label
            : preferNotToSay
            ? "prefer not to say"
            : null
        }
      >
        <Fragment>
          {fromCurrentOccupation ? (
            <Fragment>
              <div className="education-label">
                {fromCurrentOccupation.label}
              </div>
              <div className="education-source">
                according to your{" "}
                <Link to="../affiliation/current">
                  current occupation/affiliation information
                </Link>{" "}
                which you can{" "}
                <Link to="../affiliation/current">change here</Link>
              </div>
            </Fragment>
          ) : fromPastOccupation && fromPastOccupation.symbolic_id === "PHD" ? (
            <Fragment>
              <div className="education-label">{fromPastOccupation.label}</div>
              <div className="education-source">
                according to your{" "}
                <Link to="../affiliation/past">
                  past occupation/affiliation information
                </Link>{" "}
                which you can <Link to="../affiliation/past">change here</Link>
              </div>
            </Fragment>
          ) : (
            <Fragment>
              <Field>
                {educationLevels.map((levelInfo) => {
                  const na =
                    fromPastOccupation &&
                    levelInfo.level < fromPastOccupation.level;
                  return (
                    <Control key={levelInfo.symbolic_id}>
                      <RadioColumns
                        key={levelInfo.symbolic_id}
                        name="education-select"
                        disabled={na}
                        value={radioSelection}
                        optionValue={levelInfo.symbolic_id}
                        onChange={() => selectLevel(levelInfo)}
                      >
                        {na ? "* " : ""} {levelInfo.label}
                      </RadioColumns>
                    </Control>
                  );
                })}
                {fromPastOccupation ? (
                  <div className="education-source">
                    * = not applicable because of your past{" "}
                    {pastOccupationInfo ? (
                      pastOccupationInfo.label.toLowerCase() + " status, "
                    ) : (
                      <Link to="../affiliation/past">
                        occupation/affiliation information
                      </Link>
                    )}{" "}
                    which you can{" "}
                    <Link to="../affiliation/past">change here</Link>
                  </div>
                ) : (
                  <Control>
                    <RadioColumns
                      name="education-select"
                      value={radioSelection}
                      optionValue="PREFER_NOT_TO_SAY"
                      onChange={setPreferNotToSay}
                    >
                      Prefer not to say
                    </RadioColumns>
                  </Control>
                )}
              </Field>
            </Fragment>
          )}
          {selection && selection.symbolic_id === "PHD" ? (
            <Field>
              <Label>
                Year of PhD (or of initial faculty appointment if no PhD):
              </Label>
              <Control>
                <PromptPhdYear
                  promptProps={{
                    currentToDisplay: phdYear,
                    onChange: (value) =>
                      setValue(
                        "phd_year",
                        value === "" ? value : Number(value)
                      ),
                  }}
                ></PromptPhdYear>
              </Control>
            </Field>
          ) : null}
        </Fragment>
      </RenderField>
    </Fragment>
  );
}

export default EducationLevelComponent;
